/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { isSaveDisabled } from 'uplisting-frontend/utils/changeset';
import type { RhsRecord } from 'uplisting-frontend/utils/rhs/types';
import { getSavedRepliesRouteFor } from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  savedReply?: ModelRegistry['saved-reply'];
  record: RhsRecord;
}

interface SavedReplyFormSignature {
  Element: null;

  Args: IArgs;
}

export default class UiSavedReplyFormComponent extends Component<SavedReplyFormSignature> {
  @service router!: Services['router'];
  @service notifications!: Services['notifications'];

  @service('repositories/saved-reply')
  savedReplyRepository!: Services['repositories/saved-reply'];

  @cached
  get record(): RhsRecord {
    return this.args.record;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['saved-reply']> {
    return this.savedReplyRepository.buildChangeset(this.args.savedReply);
  }

  @cached
  get repliesRoute(): string {
    return getSavedRepliesRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get baseRoute(): string {
    return (this.router.currentRouteName ?? '').startsWith('calendar')
      ? 'calendar'
      : 'monthly';
  }

  @action
  handleWillDestroy(): void {
    this.changeset.rollback();

    if (!this.changeset.id && !this.isDestroying) {
      this.changeset.data.unloadRecord();
    }
  }

  @action
  @waitFor
  async handleFormSubmit(): Promise<void> {
    const saveDisabled = await isSaveDisabled(this.changeset);

    if (saveDisabled) {
      return;
    }

    try {
      await this.changeset.save();

      this.router.transitionTo(this.repliesRoute);

      this.notifications.info();
    } catch {
      this.notifications.error();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::SavedReply::Form': typeof UiSavedReplyFormComponent;
  }
}
